// @flow
import React from 'react';
import { connect } from 'react-redux';
import {
  filter, get, keyBy, mapValues, maxBy, partition, some, sortBy,
} from 'lodash';
import moment from 'moment';
import classnames from 'classnames';
import {
  Icon, Button, Modal, Spinner,
} from '@elements';
import type { FullAppointment } from '@types';
import {
  isEnterKey, isPresent, isSecretary, keepKeysRef, openInBrowser, UserNotifications,
} from '@helpers/helpers';
import { dashboardPageUrl } from '@context/user';
import { isStoreReview } from '../../../../../redux/modules/appVersion';
import { STATUS_ICONS } from '../../../../elements/Calendar/status';

type Status = {
  key: string,
  text: string,
  at: string,
  by?: string,
  // The status it puts the appointment in, in the agenda cards' words (Appointment#badge), or 'scheduled' for the
  // scheduling, which is none of them
  badge?: string,
  // Its own icon; otherwise its badge's, as on the agenda
  icon?: string,
  // One the user sets and unsets (Appointment::STATUS_STATES): its key is the state sent to the API
  settable?: boolean,
};

type Props = {
  fullAppointment: ?FullAppointment,
  setModal: Function,
  onChangeStatus: Function,
  _: Function,
  // isLockedForLoading: boolean,
  accessToken: string,
  userId: ?number,
  clinicId: ?number,
  canOpenHistory: boolean,
};

// Everything that can happen to an appointment, in the order of its life
const STATUSES: Array<Status> = [
  {
    key: 'scheduled', text: 'label/status/scheduled', icon: 'event_note', at: 'createdAt', by: 'createdByUserName', badge: 'scheduled',
  },
  {
    key: 'sms_sent', text: 'label/status/sms_sent', icon: 'mobile', at: 'smsSentAt',
  },
  // The patient's answer on the confirmation link
  {
    key: 'sms_confirmed', text: 'label/status/sms_confirmed', icon: 'mobile', at: 'confirmedBySmsAt', badge: 'confirmed',
  },
  {
    key: 'sms_canceled', text: 'label/status/sms_canceled', icon: 'mobile', at: 'canceledBySmsAt', badge: 'canceled',
  },
  {
    key: 'confirmed', text: 'label/status/confirmed', at: 'confirmedAt', by: 'confirmedByUserName', badge: 'confirmed', settable: true,
  },
  {
    key: 'patient_arrived',
    text: 'label/status/patient_arrived',
    at: 'patientArrivedAt',
    by: 'patientArrivedByUserName',
    badge: 'arrived',
    settable: true,
  },
  // The API doesn't say who set it
  {
    key: 'finished', text: 'label/status/patient_attended', at: 'finishedAt', badge: 'completed', settable: true,
  },
  {
    key: 'patient_missed', text: 'label/status/patient_missed', at: 'patientMissedAt', by: 'patientMissedByUserName', badge: 'missed', settable: true,
  },
  {
    key: 'canceled_by_patient',
    text: 'label/status/patient_canceled',
    at: 'canceledByPatientAt',
    by: 'canceledByPatientByUserName',
    badge: 'canceled',
    settable: true,
  },
  {
    key: 'canceled_by_doctor',
    text: 'label/status/canceled_by_doctor',
    at: 'canceledByDoctorAt',
    by: 'canceledByDoctorByUserName',
    badge: 'canceled',
    settable: true,
  },
];

const STATUS_BY_KEY = keyBy(STATUSES, 'key');

// The first one set is the appointment's status: Appointment#badge's order (missed, canceled, completed, arrived,
// confirmed), so this screen says what the agenda's cards say. None: scheduled. Of the kinds with the same badge (the
// three cancellations, the two confirmations), the latest, so the status's time and who are the last word
const CURRENT_ORDER = [
  'patient_missed', 'canceled_by_doctor', 'canceled_by_patient', 'sms_canceled',
  'finished', 'patient_arrived', 'confirmed', 'sms_confirmed',
];

type StatusDates = { [key: string]: ?string };

// When each status was set, by its key; null for one not set
const statusDates = (appointment: ?FullAppointment): StatusDates => mapValues(
  STATUS_BY_KEY,
  (status) => get(appointment, status.at) || null,
);

const statusIcon = (status: Status): string => status.icon || get(STATUS_ICONS, status.badge);

// Still on the agenda, but not happening
const isInactiveBadge = (badge: ?string): boolean => badge === 'canceled' || badge === 'missed';

// The API's badge decides when one of the statuses set gives it (what the agenda's cards show, also if its rules change
// for a store build already installed); otherwise the order above does
const currentStatus = (dates: StatusDates, apiBadge: ?string): Status => {
  const set = filter(CURRENT_ORDER, (key) => isPresent(dates[key]));
  if (!set.length) return STATUS_BY_KEY.scheduled;
  const badge = some(set, (key) => STATUS_BY_KEY[key].badge === apiBadge) ? apiBadge : STATUS_BY_KEY[set[0]].badge;
  // A tie keeps the badge order's first
  return STATUS_BY_KEY[maxBy(filter(set, (key) => STATUS_BY_KEY[key].badge === badge), (key) => moment(dates[key]).valueOf() || 0)];
};

// "28/out às 09:12 · Ana": when, and who did it when the API says
const statusMeta = (appointment: ?FullAppointment, date: ?string, status: Status, dateFormat: string): string => filter([
  date && moment(date).format(dateFormat),
  status.by && get(appointment, status.by),
], isPresent).join(' · ');

// The appointment's change log in the Painel de Controle (api dashboard/appointments#history), signed in with the
// user's token: what changed, when and by whom. This URL carries the token: it is for the user's own browser.
export const appointmentHistoryUrl = (accessToken: string, userId: any, clinicId: any, appointmentId: any): string => (
  dashboardPageUrl(accessToken, `/dashboard/users/${userId}/clinics/${clinicId}/appointments/${appointmentId}/history`)
);

// The dashboard's appointment pages let in managers and secretaries (authorize_manager_or_secretary). Not
// permissionTo.dashboard (manager || the plan's include_financial): it shows the link to doctors the page blocks, and
// hides it from secretaries on plans without the financial module, whom the page lets in. A build in App Store review
// keeps the dashboard out, as the help menu does.
export const canOpenAppointmentHistory = (state: Object): boolean => (
  (Boolean(get(state, ['context', 'user', 'advancedData', 'manager'])) || isSecretary(state)) && !isStoreReview(state)
);

// The appointment's status on top, what happened to it as a timeline (oldest first, so it ends where the status is),
// then what can still happen: the steps forward, and apart, the ones that call it off. A status set is unset from its
// place in the timeline (Desfazer), as a click on its checked row did in the old checklist.
export class StatusModal extends React.Component<Props> {
  renderAction = (status: Status) => (
    <button
      key={status.key}
      type="button"
      className={classnames('statusAction', status.badge)}
      onClick={this.changeStatus(status.key, true)}
    >
      <span className="statusIcon">
        <Icon iconName={statusIcon(status)} size={18} aria-hidden="true" />
      </span>
      <span className="statusActionLabel">{this.props._(status.text)}</span>
    </button>
  );

  render() {
    const { fullAppointment, canOpenHistory, _ } = this.props;

    const isLoading = !get(fullAppointment, 'full', false);
    // Only a saved appointment has a history
    const showHistory = canOpenHistory && isPresent(get(fullAppointment, 'id'));

    const dateFormat = _('format/status_date');
    const dates = statusDates(fullAppointment);
    const current = currentStatus(dates, get(fullAppointment, 'badge'));
    const { badge } = current;
    const currentMeta = statusMeta(fullAppointment, dates[current.key], current, dateFormat);

    // The scheduling first, then the rest as it happened (sortBy keeps the list's order for equal times)
    const happened = sortBy(
      filter(STATUSES, (status) => isPresent(dates[status.key])),
      [(status) => status.key !== 'scheduled', (status) => moment(dates[status.key]).valueOf()],
    );
    const [forward, destructive] = partition(
      filter(STATUSES, (status) => status.settable && !isPresent(dates[status.key])),
      (status) => !isInactiveBadge(status.badge),
    );

    return (
      <Modal id="patientModal" className="secondaryModal">
        <div className="modalHeader actionHeader">
          <Button className="backButton" onClick={this.handleOnBack}>
            <Icon iconName="arrow-left" className="icon" />
          </Button>
          <span className="backButtonLabel">{_('label/change_status')}</span>
        </div>
        {isLoading && <Spinner text={_('label/loading', 'global')} />}
        {!isLoading && (
          <div className="appointmentStatus" ref={this.bindKeys}>
            <div className={classnames('statusCurrent', badge)}>
              <span className="statusIcon">
                <Icon iconName={get(STATUS_ICONS, badge, statusIcon(current))} size={24} aria-hidden="true" />
              </span>
              <div className="statusText">
                <span className="statusCurrentLabel">{_(`label/badge/${badge}`)}</span>
                {isPresent(currentMeta) && <span className="statusMeta">{currentMeta}</span>}
              </div>
            </div>

            {(happened.length > 0 || showHistory) && (
              <section className="statusSection">
                <h3 className="statusSectionTitle">{_('label/status/timeline')}</h3>
                {/* The role isn't redundant on iOS: WebKit drops a list's semantics with its list-style */}
                {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                <ol className="statusTimeline" role="list">
                  {happened.map((status) => (
                    <li
                      key={status.key}
                      className={classnames('statusEvent', status.badge || 'neutral', { current: status === current })}
                    >
                      <span className="statusIcon">
                        <Icon iconName={statusIcon(status)} size={16} aria-hidden="true" />
                      </span>
                      <div className="statusText">
                        <span className="statusEventLabel">{_(status.text)}</span>
                        <span className="statusMeta">{statusMeta(fullAppointment, dates[status.key], status, dateFormat)}</span>
                      </div>
                      {status.settable && (
                        <button
                          type="button"
                          className="statusUndo"
                          aria-label={`${_('action/status/undo')}: ${_(status.text)}`}
                          onClick={this.changeStatus(status.key, false)}
                        >
                          {_('action/status/undo')}
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
                {/* "Histórico de alterações": the whole change log, in the Painel de Controle */}
                {showHistory && (
                  <button
                    type="button"
                    className="statusHistoryLink"
                    aria-label={_('action/status/history_label')}
                    onClick={this.openHistory}
                  >
                    <span>{_('action/status/history')}</span>
                    <Icon iconName="open_in_new" className="icon" size={16} aria-hidden="true" />
                  </button>
                )}
              </section>
            )}

            {(forward.length > 0 || destructive.length > 0) && (
              <section className="statusSection">
                <h3 className="statusSectionTitle">{_('label/status/update')}</h3>
                {forward.length > 0 && <div className="statusActions">{forward.map(this.renderAction)}</div>}
                {/* Missed and canceled outrank these (Appointment#badge): they are recorded, the status stays */}
                {forward.length > 0 && isInactiveBadge(badge) && (
                  <p className="statusNote">{_(`label/status/stays/${badge}`)}</p>
                )}
                {destructive.length > 0 && (
                  <div className="statusActions destructive">{destructive.map(this.renderAction)}</div>
                )}
                {/* Missed outranks the cancellations too */}
                {destructive.length > 0 && badge === 'missed' && (
                  <p className="statusNote">{_('label/status/stays/missed')}</p>
                )}
              </section>
            )}
          </div>
        )}
      </Modal>
    );
  }

  // A click or a tap (detail > 0) lets go of the focus: desktop Chrome keeps it on a clicked button, and the Enter
  // that saves the appointment would only open the page again (bindKeys). Enter or Space on it (detail 0) keep it
  // there, so the keyboard doesn't lose its place.
  openHistory = (event: Object) => {
    if (event.detail > 0) event.currentTarget.blur();
    const {
      accessToken, userId, clinicId, fullAppointment,
    } = this.props;
    if (!openInBrowser(appointmentHistoryUrl(accessToken, userId, clinicId, get(fullAppointment, 'id')))) {
      UserNotifications.i18nTextAlert('error/browser_unavailable');
    }
  };

  // Enter on one of the panel's buttons is that button's (the browser clicks it), not Salvar: AppointmentModalForm
  // takes Enter as Salvar in a keydown listener on document. Nothing else in the panel takes the focus.
  bindKeys = keepKeysRef((event) => isEnterKey(event) && get(event, ['target', 'tagName']) === 'BUTTON');

  // Sets a status (the actions) or unsets it (the timeline's Desfazer). Setting a cancellation asks whether to take
  // the appointment off the agenda too (hidden)
  changeStatus = (type: string, value: boolean) => () => {
    const data = { type };
    if ((type === 'canceled_by_patient' || type === 'canceled_by_doctor') && value) {
      UserNotifications.confirmI18n('alert/hide/appointment', null, null, true, false)
        .then((confirmed) => {
          if (confirmed) {
            this.props.onChangeStatus(data, value, true);
          } else {
            this.props.onChangeStatus(data, value);
          }
        });
    } else {
      this.props.onChangeStatus(data, value);
    }
  };

  handleOnBack = () => this.props.setModal(null);
}

const mapStateToProps = (state) => ({
  accessToken: state.auth.token,
  userId: get(state, ['context', 'user', 'id']),
  clinicId: get(state, ['context', 'clinics', 'activeClinic', 'id']),
  canOpenHistory: canOpenAppointmentHistory(state),
});

export default connect(mapStateToProps)(StatusModal);
